import _ from "lodash";
import {
  Text,
  Group,
  Badge,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import React, { useCallback } from "react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";

import {
  IconCake,
  IconMailbox,
  IconFaceId,
  IconPhone,
  IconLanguage,
  IconFlag,
  IconSchool,
  IconBarcode,
} from "@tabler/icons";

import { useServerApi } from "../hooks/userServerApi";
import { formatPhoneDisplay } from "../helpers/phoneUtils";
import { useTranslation } from "react-i18next";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { getEntityTabLabel } from "../utils/tabNavigation";
import { resolveEntityPath } from "../utils/resolveEntityPath";

const getClientRecordId = (client, form, fieldName) => {
  if (!client) return null;
  if (client._id) return client._id;

  if (form && fieldName) {
    const clientRefPath = fieldName.replace(/SnapShot$/i, "");
    if (clientRefPath !== fieldName) {
      const clientRef = _.get(form.values, clientRefPath);
      if (clientRef?._id) return clientRef._id;
    }
  }

  return null;
};

const OrderApplicantDetail = ({
  form,
  name = "client",
  size = "sm",
  ...props
}) => {
  const [client, setClient] = useState();
  const [api] = useServerApi();
  const { t } = useTranslation();
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const location = useLocation();
  let formValue = _.get(form.values, name);

  const openClientTab = useCallback(
    (clientRecordId, clientData) => {
      const pathname = resolveEntityPath(
        "client",
        auth()?.userRole,
        location.pathname,
      );
      if (!pathname || !clientRecordId) return;

      if (tabNav.enabled) {
        tabNav.openDetail({
          pathname,
          id: clientRecordId,
          mode: "edit",
          label: getEntityTabLabel(clientData, clientRecordId, "client"),
        });
        return;
      }

      window.open(`${pathname}?id=${clientRecordId}&mode=edit`, "_blank");
    },
    [auth, location.pathname, tabNav],
  );

  const professionalInvestor = form?.values?.professionalInvestor;
  const mainlandChineseVisitor = form?.values?.mainlandChineseVisitor;

  useEffect(() => {
    typeof formValue === "object"
      ? setClient(formValue)
      : fetchClient(formValue);
  }, [formValue]);

  const fetchClient = async (clientId) => {
    if (!clientId) return;
    let client = await api.getById({ apiEntity: "client", id: clientId });
    setClient(client);
  };

  const ClientInfo = ({ client, fieldName }) => {
    const {
      name,
      cname,
      status,
      vulnerable,
      orders,
      email,
      phone,
      address,
      language,
      id,
      idType,
      educationLevel,
      birthday,
      residentialCountry,
      placeOfBirth,
      code,
      contactPerson,
    } = client;

    const clientRecordId = getClientRecordId(client, form, fieldName);
    const iconSize = 18;
    const hasValue = (value) =>
      value != null &&
      String(value).trim() !== "" &&
      String(value).trim() !== "0";

    return (
      <>
        {code && (
          <Group mt={10} noWrap>
            <IconBarcode size={iconSize} />
            {clientRecordId ? (
              <UnstyledButton onClick={() => openClientTab(clientRecordId, client)}>
                <Text size={size} weight={500} transform="uppercase">
                  {code}
                </Text>
              </UnstyledButton>
            ) : (
              <Text size={size} weight={500} transform="uppercase">
                {code}
              </Text>
            )}
          </Group>
        )}

        <Group mt={10} noWrap align="flex-start">
          <IconFaceId size={iconSize} style={{ flexShrink: 0 }} />
          {name && (
            <Text size={size} weight={500} transform="capitalize">
              {name}
            </Text>
          )}
        </Group>

        {cname && (
          <Group mt={10} noWrap>
            <IconLanguage size={iconSize} />
            <Text size={size} weight={500}>
              {cname}
            </Text>
          </Group>
        )}

        {id && (
          <Group mt={10} noWrap>
            <IconBarcode size={iconSize} />
            <Text size={size} weight={500} transform="uppercase">
              {id}
            </Text>
          </Group>
        )}
        {contactPerson && (
          <Group mt={10} noWrap>
            <IconPhone size={iconSize} />
            <Text size={size} weight={500} transform="capitalize">
              {contactPerson}
            </Text>
          </Group>
        )}

        {email && (
          <Group mt={10} noWrap>
            <IconMailbox size={iconSize} />
            <Text size={size} weight={500} transform="lowercase">
              {email}
            </Text>
          </Group>
        )}

        {formatPhoneDisplay(phone) && (
          <Group mt={10} noWrap>
            <IconPhone size={iconSize} />
            <Text size={size} weight={500} transform="lowercase">
              {formatPhoneDisplay(phone)}
            </Text>
          </Group>
        )}

        {language && (
          <Group mt={10} noWrap>
            <IconLanguage size={iconSize} />
            <Text size={size} weight={500} transform="capitalize">
              {language}
            </Text>
          </Group>
        )}

        {hasValue(placeOfBirth) && (
          <Group mt={10} noWrap>
            <IconFlag size={iconSize} />
            <Text size={size} weight={500} transform="capitalize">
              {placeOfBirth}
            </Text>
          </Group>
        )}

        {educationLevel && (
          <Group mt={10} noWrap>
            <IconSchool size={iconSize} />
            <Text size={size} weight={500} transform="capitalize">
              {educationLevel}
            </Text>
          </Group>
        )}

        {birthday && moment(birthday).isValid() && (
          <Group mt={10} noWrap>
            <IconCake size={18} />
            <Text size={size} weight={500}>
              {moment(birthday).format("LL")}
            </Text>
          </Group>
        )}
      </>
    );
  };

  return (
    <>
      {(professionalInvestor || mainlandChineseVisitor) && (
        <Group spacing={4} mb="sm">
          {professionalInvestor && (
            <Tooltip label={t("Professional Investor (PI)")}>
              <Badge size="xs" color="yellow" radius={0}>
                PI
              </Badge>
            </Tooltip>
          )}
          {mainlandChineseVisitor && (
            <Tooltip label={t("Mainland Chinese Visitor (MCV)")}>
              <Badge size="xs" color="orange" radius={0}>
                MCV
              </Badge>
            </Tooltip>
          )}
        </Group>
      )}
      {client && <ClientInfo client={client} fieldName={name} />}
    </>
  );
};

export default OrderApplicantDetail;
